import { type PostContent } from '@backend/lib/platforms/youtube'

type Props = {
  postContent: PostContent
  account: { id: string; displayName: string; imageUrl?: string; subline?: string } | undefined
  postAsShort?: boolean
  _onStartAuth: () => void
}

export function WorkspacePreviewYoutube({
  account,
  postContent,
  postAsShort,
  _onStartAuth,
}: Props) {
  if (postAsShort) {
    return <YouTubeShortsPreview account={account} postContent={postContent} />
  }

  return (
    <div className="mx-auto flex h-full w-full flex-col gap-3 p-3 text-black">
      {/* Video player */}
      <div className="-mx-3 flex items-center justify-center bg-black">
        {postContent?.url ? (
          <div className="relative flex aspect-video w-full items-center justify-center">
            <video
              src={postContent.url}
              className="h-full w-full object-contain"
              controls
              autoPlay
              muted
              playsInline
            />
          </div>
        ) : (
          <div className="flex aspect-video w-full items-center justify-center bg-gray-900">
            <p className="text-gray-400">No video</p>
          </div>
        )}
      </div>

      {/* Channel header */}
      <div className="flex items-center justify-between gap-3 px-3">
        <div className="flex items-center gap-3">
          {/* Channel avatar */}
          <div className="h-10 w-10 overflow-hidden rounded-full bg-gray-200">
            {account?.imageUrl ? (
              <img src={account.imageUrl} alt="Channel" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-300">
                <span className="text-sm text-gray-600">
                  {account?.displayName?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
            )}
          </div>

          {/* Channel name and subscribers */}
          <div className="flex flex-col">
            <span className="text-sm font-semibold">{account?.displayName || 'Your Channel'}</span>
            <span className="text-xs text-gray-600">
              {account?.subline || 'No subscribers yet'}
            </span>
          </div>
        </div>

        {/* Subscribe button */}
        <button className="rounded-full bg-[#CC0000] px-4 py-2 text-sm font-medium text-white hover:bg-[#B00000]">
          Subscribe
        </button>
      </div>

      {/* Video info */}
      <div className="space-y-2 px-3">
        {/* Title */}
        {postContent?.title && (
          <h3 className="line-clamp-2 text-base font-semibold">{postContent.title}</h3>
        )}

        {/* Stats */}
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>1,234 views</span>
          <span>•</span>
          <span>2 hours ago</span>
        </div>

        {/* Description */}
        {postContent?.description && (
          <div className="rounded-lg bg-gray-100 p-3">
            <p className="line-clamp-3 whitespace-pre-wrap text-xs text-gray-800">
              {postContent.description}
            </p>
          </div>
        )}

        {/* Action buttons row */}
        <div className="flex items-center gap-2 pt-2">
          {/* Like */}
          <button className="flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 hover:bg-gray-200">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
            </svg>
            <span className="text-sm font-medium">234</span>
          </button>

          {/* Dislike */}
          <button className="rounded-full bg-gray-100 p-2 hover:bg-gray-200">
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ transform: 'rotate(180deg)' }}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
            </svg>
          </button>

          {/* Share */}
          <button className="flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 hover:bg-gray-200">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
            </svg>
            <span className="text-sm font-medium">Share</span>
          </button>
        </div>
      </div>
    </div>
  )
}

function YouTubeShortsPreview({
  account,
  postContent,
}: {
  account: Props['account']
  postContent: PostContent
}) {
  return (
    <div className="mx-auto flex h-full w-full flex-col items-center justify-center p-3">
      <div className="aspect-9/16 relative flex max-h-[600px] w-full max-w-[340px] flex-col overflow-hidden rounded-lg bg-black">
        {/* Video */}
        <div className="relative h-full w-full">
          {postContent?.url ? (
            <video
              src={postContent.url}
              className="h-full w-full object-contain"
              controls
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900">
              <p className="text-gray-400">No video</p>
            </div>
          )}
        </div>

        {/* Right-side engagement buttons */}
        <div className="absolute bottom-20 right-3 flex flex-col items-center gap-5">
          <button className="flex flex-col items-center gap-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                />
              </svg>
            </div>
            <span className="text-xs font-medium text-white">234</span>
          </button>

          <button className="flex flex-col items-center gap-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ transform: 'rotate(180deg)' }}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                />
              </svg>
            </div>
          </button>

          <button className="flex flex-col items-center gap-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <span className="text-xs font-medium text-white">89</span>
          </button>

          <button className="flex flex-col items-center gap-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm">
              <svg
                className="h-7 w-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
            </div>
            <span className="text-xs font-medium text-white">Share</span>
          </button>
        </div>

        {/* Bottom overlay: channel + caption */}
        <div className="absolute bottom-3 left-3 right-16 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 overflow-hidden rounded-full border-2 border-white">
              {account?.imageUrl ? (
                <img src={account.imageUrl} alt="Channel" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-700">
                  <span className="text-xs text-white">
                    {account?.displayName?.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>
              )}
            </div>
            <span className="text-sm font-semibold text-white">
              {account?.displayName || 'Your Channel'}
            </span>
          </div>
          {postContent?.title && (
            <p className="line-clamp-2 text-sm text-white">{postContent.title}</p>
          )}
        </div>
      </div>
    </div>
  )
}
